import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

/**
 * SPEC §8 — "Buttons: outline only, no fill, no border-radius, animated
 * corner borders using currentColor. Olive fill is permitted *only* on the
 * primary buy action."
 *
 * `outline` is the default and the one you should reach for. `solid-olive` is
 * reserved for the single buy CTA on a page — if a screen has two of them,
 * one of them is wrong.
 *
 * The border animation lives in `.kf-corners` (src/styles/main.scss): a grey
 * 1px border at rest, traced over on hover by four `currentColor` strips that
 * chase each other around the box. It belongs to the *outline* treatment
 * only — see `solid-olive` below.
 *
 * Type is Catamaran, not the Raleway heading face — see the base class.
 */

/**
 * The base class strips the browser's focus ring, because on the outline
 * variants `.kf-corners` closing into a frame IS the focus affordance. Any
 * variant without that class has to bring its own, or it has none at all —
 * this is the site's global treatment from `:focus-visible` in main.scss.
 */
const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-1 " +
  "focus-visible:outline-offset-[3px] focus-visible:outline-kf-ink";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2.5 rounded-none " +
    // Catamaran, not the heading face — every button on kuebefarm.lu is
    // Catamaran (SPEC §8). It is rounder and more geometric than Raleway.
    "font-button font-medium uppercase tracking-brand " +
    "whitespace-nowrap select-none " +
    "transition-colors duration-200 " +
    "focus-visible:outline-none focus-visible:ring-0 " +
    "disabled:pointer-events-none disabled:opacity-40 " +
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        /** Default. Ink outline with animated corner borders. */
        outline: "kf-corners bg-transparent text-kf-ink hover:text-kf-black",
        /**
         * Same mechanics, inverted — for use over photography, where the grey
         * resting border would disappear into the picture.
         */
        "outline-inverse":
          "kf-corners bg-transparent text-kf-white hover:text-kf-white " +
          "[--kf-corner-rest:rgb(255_255_255/0.45)]",
        /**
         * The ONE accent. Buy action only.
         *
         * Deliberately WITHOUT `.kf-corners`. That class paints its eight
         * strips in `currentColor` as a background-image: over a transparent
         * outline button they are the frame, but over the olive fill they are
         * white hairlines crawling across a solid block, and on hover they
         * grow to 51% a side and cut the button in half. The fill already is
         * the shape — there is no frame left to draw.
         */
        "solid-olive":
          "bg-kf-olive text-kf-white hover:bg-kf-olive-dark " +
          FOCUS_RING,
        /** Quiet secondary action — no frame until hovered. */
        ghost:
          "bg-transparent text-kf-secondary hover:bg-kf-cream hover:text-kf-ink " +
          FOCUS_RING,
        /**
         * Inline text action. Underline draws in from the left.
         *
         * Carries no font-size of its own — that belongs to `size`, below, so
         * a caller can pick one instead of overriding a hard-coded value from
         * the outside.
         */
        link:
          "kf-underline bg-transparent px-0 text-kf-ink hover:text-kf-black " +
          "tracking-wide2",
      },
      size: {
        sm: "h-9 px-4 text-[11px] [&_svg]:size-3.5",
        md: "h-11 px-7 text-[12px] [&_svg]:size-4",
        lg: "h-14 px-10 text-[13px] [&_svg]:size-4",
      },
    },
    compoundVariants: [
      // The link variant is text, not a box — it must not carry button
      // metrics, and it runs a step larger than the boxed sizes because there
      // is no frame around it to give the words presence.
      { variant: "link", size: "sm", className: "h-auto px-0 text-[12px]" },
      { variant: "link", size: "md", className: "h-auto px-0 text-[13px]" },
      { variant: "link", size: "lg", className: "h-auto px-0 text-[14px]" },
    ],
    defaultVariants: {
      variant: "outline",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a `<button>` (e.g. a router Link). */
  asChild?: boolean;
  /**
   * The button's own work is in flight. Non-interactive, but styled as
   * *working* rather than *unavailable*.
   *
   * Deliberately not `disabled`: that drops the control to 40% opacity, which
   * is the site's vocabulary for "you cannot have this" — a washed-out primary
   * CTA the instant someone commits to buying reads as a failure. `disabled`
   * also drops the button out of the accessibility tree and can move focus
   * mid-action. `aria-disabled` keeps it announced and focusable where it is,
   * and the click is simply not wired up while busy.
   */
  busy?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      busy = false,
      disabled,
      onClick,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        disabled={disabled}
        aria-disabled={busy || disabled || undefined}
        aria-busy={busy || undefined}
        onClick={busy ? undefined : onClick}
        className={cn(
          buttonVariants({ variant, size }),
          busy && "cursor-progress",
          className
        )}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
